// A single chat message bubble. Extracted from ChatDrawer.tsx — pure, renders
// from the `msg` prop only. Handles the four roles (system_meta brief bubble,
// user, assistant w/ tool_use streaming, tool_result w/ artifacts).

import { HtmlArtifact, type ArtifactDescriptor } from '../HtmlArtifact';
import { MarkdownBlock } from './Markdown';
import { type ChatMessage } from './types';

export function Message({ msg }: { msg: ChatMessage }) {
  if (msg.role === 'system_meta') {
    // Brief-artifact bubble. Distinct emerald accent so it stands out from chat.
    const tc = (msg as { toolCalls?: { kind?: string; brief_id?: string; project_name?: string; version?: number; cost_usd?: number; length_chars?: number } | null }).toolCalls;
    const briefMeta = tc && tc.kind === 'brief' ? tc : null;
    const text = msg.content.find((b) => b.type === 'text' && typeof b.text === 'string')?.text ?? '';
    return (
      <div className="w-full">
        {briefMeta && (
          <div className="flex items-center gap-2 mb-1 text-xs text-emerald-700 flex-wrap">
            <span className="px-2 py-0.5 bg-emerald-100 rounded-full font-semibold">
              📄 Bid Brief v{briefMeta.version ?? '?'}
            </span>
            {typeof briefMeta.cost_usd === 'number' && (
              <span className="text-slate-500">${briefMeta.cost_usd.toFixed(2)}</span>
            )}
            {typeof briefMeta.length_chars === 'number' && (
              <span className="text-slate-500">· {Math.round(briefMeta.length_chars / 1000)}k chars</span>
            )}
            {briefMeta.brief_id && (
              <a
                href={`/briefs/${briefMeta.brief_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto px-2 py-0.5 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                title="Open in print-friendly viewer (Save as PDF from there)"
              >
                ↗ Open / Print
              </a>
            )}
          </div>
        )}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-w-full overflow-hidden">
          <MarkdownBlock text={text} />
        </div>
      </div>
    );
  }
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-brand-navy text-white rounded-lg px-3 py-2">
          {msg.content.map((b, i) =>
            b.type === 'text' && typeof b.text === 'string'
              ? <MarkdownBlock key={i} text={b.text} />
              : null,
          )}
        </div>
      </div>
    );
  }
  if (msg.role === 'assistant') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] bg-slate-100 text-slate-900 rounded-lg px-3 py-2">
          {msg.content.map((b, i) => {
            if (b.type === 'text' && typeof b.text === 'string') {
              return <MarkdownBlock key={i} text={b.text} />;
            }
            if (b.type === 'tool_use') {
              // Innovation Audit #6 — live tool-input streaming. If the input
              // is still streaming, b.input is { _partial_json: string }; show
              // it raw with a pulsing cursor. Once the final input lands, the
              // _partial_json key is gone and we render the parsed JSON.
              //
              // If the model hit max_tokens mid-input, the client tags the
              // input with __input_parse_error — show a clear truncation
              // notice instead of an empty {} which the user can't interpret.
              const inputObj = b.input as {
                _partial_json?: string;
                __input_parse_error?: string;
                __raw_length?: number;
                __raw_truncated_input?: string;
              } | undefined;
              const partial = inputObj?._partial_json;
              const isStreaming = typeof partial === 'string';
              const isTruncated = typeof inputObj?.__input_parse_error === 'string';
              return (
                <details key={i} className="mt-2 text-xs" open={isStreaming || isTruncated}>
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                    🔧 <span className="font-mono">{b.name}</span>
                    {isStreaming && <span className="ml-2 text-amber-600 animate-pulse">…streaming input</span>}
                    {isTruncated && <span className="ml-2 text-rose-600">⚠ input truncated</span>}
                  </summary>
                  {isTruncated ? (
                    <div className="bg-rose-50 border border-rose-200 rounded p-2 mt-1 text-[11px] text-rose-900">
                      <div className="font-semibold mb-1">Tool input was truncated by max_tokens.</div>
                      <div className="text-rose-800">
                        The model emitted ~{inputObj?.__raw_length ?? 0} chars of JSON before
                        running out of output budget — too short to form valid input. The
                        assistant should retry with a smaller payload (or split into pieces).
                      </div>
                      {inputObj?.__raw_truncated_input && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-rose-700">Show raw partial</summary>
                          <pre className="bg-white border border-rose-200 rounded p-2 mt-1 overflow-x-auto text-[10px] text-slate-700">
                            {inputObj.__raw_truncated_input}
                          </pre>
                        </details>
                      )}
                    </div>
                  ) : (
                    <pre className="bg-white border border-slate-200 rounded p-2 mt-1 overflow-x-auto text-[10px]">
                      {isStreaming
                        ? (partial || '…')
                        : JSON.stringify(b.input, null, 2)}
                    </pre>
                  )}
                </details>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  }
  if (msg.role === 'tool_result') {
    // First pass: pluck any artifact descriptors out of the result payloads.
    const artifacts: ArtifactDescriptor[] = [];
    for (const b of msg.content) {
      if (typeof b.content !== 'string') continue;
      try {
        const parsed = JSON.parse(b.content) as { artifact?: ArtifactDescriptor };
        if (parsed && typeof parsed === 'object' && parsed.artifact && typeof parsed.artifact.url === 'string') {
          artifacts.push(parsed.artifact);
        }
      } catch {
        // ignore — fall through to raw JSON viewer below
      }
    }
    return (
      <div className="space-y-2">
        {artifacts.map((art, i) => (
          <HtmlArtifact key={`art-${i}`} artifact={art} />
        ))}
        <details className="text-xs text-slate-500 ml-4">
          <summary className="cursor-pointer hover:text-slate-700">📤 tool result</summary>
          <pre className="bg-slate-50 border border-slate-200 rounded p-2 mt-1 overflow-x-auto text-[10px]">
            {msg.content.map((b, i) => (
              <span key={i}>
                {typeof b.content === 'string' ? b.content : JSON.stringify(b.content, null, 2)}
                {'\n'}
              </span>
            ))}
          </pre>
        </details>
      </div>
    );
  }
  return null;
}
